// ═══════════════════════════════════════════════════════════════
// OrgStructurePanel.tsx – szervezeti egységek, pozíciók és munkatársak egy nézetben
// Megjelenítés: blokk (beágyazott kártyák) / lenyíló (fa) / táblázat (behúzott sorok)
// Minden szinten: felvétel (alegység, pozíció, munkatárs), szerkesztés, törlés
// ═══════════════════════════════════════════════════════════════
import React, { useMemo, useState } from 'react';
import type { OrgUnit } from '../../types';
import {
  childUnits, unitPositions, positionPersons, unitLoosePersons, unitLeaders, unitStaffCount,
  type OrgModel, type OrgPerson, type OrgPosition,
} from './orgModel';
import {
  Building2, Briefcase, User, Plus, Edit2, Trash2, Crown, ChevronDown, ChevronRight, Phone, Mail, MapPin, FolderPlus, UserPlus,
} from 'lucide-react';

export type OrgViewMode = 'blocks' | 'accordion' | 'table';

export interface OrgStructureHandlers {
  onAddUnit: (parentId: number | null) => void;
  onEditUnit: (u: OrgUnit) => void;
  onDeleteUnit: (u: OrgUnit) => void;
  onAddPosition: (unitId: number | null) => void;
  onEditPosition: (p: OrgPosition) => void;
  onDeletePosition: (p: OrgPosition) => void;
  onAddPerson: (unitId: number | null, positionId: number | null) => void;
  onEditPerson: (p: OrgPerson) => void;
  onDeletePerson: (p: OrgPerson) => void;
}

interface Props {
  model: OrgModel;
  mode: OrgViewMode;
  search: string;
  canEdit: boolean;
  h: OrgStructureHandlers;
}

const norm = (s: unknown) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const IconBtn: React.FC<{ title: string; onClick: () => void; danger?: boolean; children: React.ReactNode }> = ({ title, onClick, danger, children }) => (
  <button type="button" title={title} aria-label={title} onClick={(e) => { e.stopPropagation(); onClick(); }}
    className={`p-1 rounded-md transition cursor-pointer ${danger ? 'text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10' : 'text-slate-400 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
    {children}
  </button>
);

const ContactBits: React.FC<{ phone?: string | null; email?: string | null; room?: string | null; address?: string | null }> = ({ phone, email, room, address }) => (
  <span className="inline-flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-slate-500 dark:text-slate-400">
    {phone && <a href={`tel:${phone}`} className="inline-flex items-center gap-1 hover:text-indigo-600"><Phone className="w-3 h-3" />{phone}</a>}
    {email && <a href={`mailto:${email}`} className="inline-flex items-center gap-1 hover:text-indigo-600 break-all"><Mail className="w-3 h-3" />{email}</a>}
    {room && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{room}</span>}
    {address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{address}</span>}
  </span>
);

export const OrgStructurePanel: React.FC<Props> = ({ model, mode, search, canEdit, h }) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const q = norm(search.trim());

  // keresés: a találatot tartalmazó egységek és őseik látszanak
  const visible = useMemo(() => {
    if (!q) return null;
    const hit = new Set<number>();
    const matchUnit = (u: OrgUnit) => norm(`${u.name} ${u.code || ''} ${u.email || ''} ${u.phone || ''}`).includes(q)
      || unitPositions(model, u.id).some((p) => norm(p.title).includes(q) || positionPersons(model, p.id).some((pr) => norm(`${pr.name} ${pr.title || ''} ${pr.email || ''} ${pr.phone || ''} ${pr.room || ''}`).includes(q)))
      || unitLoosePersons(model, u.id).some((pr) => norm(`${pr.name} ${pr.title || ''} ${pr.email || ''} ${pr.phone || ''} ${pr.room || ''}`).includes(q));
    for (const u of model.units) if (matchUnit(u)) {
      let cur: OrgUnit | undefined = u; let guard = 0;
      while (cur && guard++ < 20) { hit.add(cur.id); cur = cur.parent_id ? model.units.find((x) => x.id === cur!.parent_id) : undefined; }
    }
    return hit;
  }, [q, model]);
  const personHit = (p: OrgPerson) => !q || norm(`${p.name} ${p.title || ''} ${p.email || ''} ${p.phone || ''} ${p.room || ''}`).includes(q);

  const isOpen = (key: string) => (q ? true : !collapsed[key]);
  const toggle = (key: string) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  const kidsOf = (pid: number | null) => childUnits(model, pid).filter((u) => !visible || visible.has(u.id));

  const unitActions = (u: OrgUnit) => canEdit && (
    <span className="inline-flex items-center gap-0.5">
      <IconBtn title="Alegység felvétele" onClick={() => h.onAddUnit(u.id)}><FolderPlus className="w-3.5 h-3.5" /></IconBtn>
      <IconBtn title="Pozíció felvétele ebbe az egységbe" onClick={() => h.onAddPosition(u.id)}><Briefcase className="w-3.5 h-3.5" /></IconBtn>
      <IconBtn title="Munkatárs felvétele ebbe az egységbe" onClick={() => h.onAddPerson(u.id, null)}><UserPlus className="w-3.5 h-3.5" /></IconBtn>
      <IconBtn title="Egység szerkesztése" onClick={() => h.onEditUnit(u)}><Edit2 className="w-3.5 h-3.5" /></IconBtn>
      <IconBtn title="Egység törlése" danger onClick={() => h.onDeleteUnit(u)}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
    </span>
  );
  const posActions = (p: OrgPosition) => canEdit && (
    <span className="inline-flex items-center gap-0.5">
      <IconBtn title="Munkatárs felvétele erre a pozícióra" onClick={() => h.onAddPerson(p.org_unit_id, p.id)}><UserPlus className="w-3.5 h-3.5" /></IconBtn>
      <IconBtn title="Pozíció szerkesztése" onClick={() => h.onEditPosition(p)}><Edit2 className="w-3.5 h-3.5" /></IconBtn>
      <IconBtn title="Pozíció törlése" danger onClick={() => h.onDeletePosition(p)}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
    </span>
  );
  const personActions = (p: OrgPerson) => canEdit && (
    <span className="inline-flex items-center gap-0.5">
      <IconBtn title="Munkatárs szerkesztése" onClick={() => h.onEditPerson(p)}><Edit2 className="w-3.5 h-3.5" /></IconBtn>
      <IconBtn title="Munkatárs törlése" danger onClick={() => h.onDeletePerson(p)}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
    </span>
  );

  const unitBadges = (u: OrgUnit) => (
    <>
      {u.placement === 'beside' && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-800">törzskari</span>}
      {u.code && <span className="text-[10px] font-mono text-slate-400">{u.code}</span>}
      <span className="text-[10px] text-slate-400">{unitStaffCount(model, u.id)} fő</span>
    </>
  );

  const leaderLine = (u: OrgUnit) => {
    const ls = unitLeaders(model, u.id).flatMap((l) => l.persons.map((p) => `${p.name} (${p.title || l.position.title})`));
    return ls.length ? <span className="inline-flex items-center gap-1 text-[11px] text-amber-800 dark:text-amber-300"><Crown className="w-3 h-3" />{ls.join(', ')}</span> : null;
  };

  // ── pozíció + munkatársai (blokk és lenyíló nézet) ──
  const positionRow = (p: OrgPosition) => {
    const ppl = positionPersons(model, p.id).filter(personHit);
    return (
      <div key={`pos-${p.id}`} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2.5 py-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-800 dark:text-slate-200 min-w-0">
            {p.is_leader ? <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" /> : <Briefcase className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
            <span className="truncate" title={p.job_description || p.title}>{p.title}</span>
            {p.multi_person ? <span className="text-[10px] font-normal text-slate-400">(több fő)</span> : null}
          </span>
          {posActions(p)}
        </div>
        {ppl.length > 0 ? (
          <div className="mt-1 space-y-1 pl-5">
            {ppl.map((pr) => (
              <div key={pr.id} className="flex items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="text-xs text-slate-900 dark:text-white inline-flex items-center gap-1"><User className="w-3 h-3 text-slate-400" />{pr.name}{pr.title && pr.title !== p.title ? <span className="text-slate-500"> – {pr.title}</span> : null}</span>
                  <span className="block pl-4"><ContactBits phone={pr.phone} email={pr.email} room={pr.room} /></span>
                </span>
                {personActions(pr)}
              </div>
            ))}
          </div>
        ) : !q && <div className="pl-5 mt-0.5 text-[11px] italic text-slate-400">betöltetlen</div>}
      </div>
    );
  };
  const loosePeople = (unitId: number | null) => {
    const ppl = unitLoosePersons(model, unitId).filter(personHit);
    if (!ppl.length) return null;
    return (
      <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 px-2.5 py-1.5">
        <div className="text-[11px] font-semibold text-slate-500 mb-1">Pozíció nélküli munkatársak</div>
        {ppl.map((pr) => (
          <div key={pr.id} className="flex items-center justify-between gap-2">
            <span className="min-w-0">
              <span className="text-xs text-slate-900 dark:text-white inline-flex items-center gap-1"><User className="w-3 h-3 text-slate-400" />{pr.name}{pr.title ? <span className="text-slate-500"> – {pr.title}</span> : null}</span>
              <span className="block pl-4"><ContactBits phone={pr.phone} email={pr.email} room={pr.room} /></span>
            </span>
            {personActions(pr)}
          </div>
        ))}
      </div>
    );
  };
  const unitBody = (u: OrgUnit) => {
    const poss = unitPositions(model, u.id).filter((p) => !q || norm(p.title).includes(q) || positionPersons(model, p.id).some(personHit));
    return (
      <div className="space-y-1.5">
        {(u.phone || u.email || u.address) && <ContactBits phone={u.phone} email={u.email} address={u.address} />}
        {u.description && <p className="text-[11px] text-slate-500 dark:text-slate-400">{u.description}</p>}
        {poss.map(positionRow)}
        {loosePeople(u.id)}
        {!poss.length && !unitLoosePersons(model, u.id).length && !q && (
          <p className="text-[11px] italic text-slate-400">Még nincs pozíció vagy munkatárs.{canEdit ? ' A fejléc ikonjaival vehetsz fel.' : ''}</p>
        )}
      </div>
    );
  };

  // ── BLOKK nézet ──
  const renderBlock = (u: OrgUnit, depth: number): React.ReactNode => (
    <div key={u.id} className={`rounded-2xl border bg-white dark:bg-slate-900 shadow-xs ${depth === 0 ? 'border-slate-200 dark:border-slate-800' : 'border-slate-200/80 dark:border-slate-800/80'} ${u.placement === 'beside' ? 'border-dashed' : ''}`}>
      <div className="px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">{u.name}</h3>
            {unitBadges(u)}
          </div>
          {leaderLine(u)}
        </div>
        {unitActions(u)}
      </div>
      <div className="p-3 space-y-3">
        {unitBody(u)}
        {kidsOf(u.id).length > 0 && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 pl-3 border-l-2 border-indigo-100 dark:border-indigo-900">
            {kidsOf(u.id).map((k) => renderBlock(k, depth + 1))}
          </div>
        )}
      </div>
    </div>
  );

  // ── LENYÍLÓ nézet ──
  const renderAccordion = (u: OrgUnit, depth: number): React.ReactNode => {
    const key = `u${u.id}`;
    const open = isOpen(key);
    const kids = kidsOf(u.id);
    return (
      <div key={u.id} className="space-y-1.5">
        <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border bg-white dark:bg-slate-900 cursor-pointer select-none ${u.placement === 'beside' ? 'border-dashed border-violet-300 dark:border-violet-800' : 'border-slate-200 dark:border-slate-800'}`}
          onClick={() => toggle(key)} role="button" aria-expanded={open}>
          <span className="flex items-center gap-1.5 min-w-0 flex-wrap">
            {open ? <ChevronDown className="w-4 h-4 text-indigo-600 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
            <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
            <span className="text-sm font-bold text-slate-900 dark:text-white">{u.name}</span>
            {unitBadges(u)}
            {kids.length > 0 && <span className="text-[10px] text-slate-400">· {kids.length} alegység</span>}
          </span>
          {unitActions(u)}
        </div>
        {open && (
          <div className="ml-4 pl-3 border-l-2 border-indigo-100 dark:border-indigo-900 space-y-1.5">
            {leaderLine(u)}
            {unitBody(u)}
            {kids.map((k) => renderAccordion(k, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // ── TÁBLÁZAT nézet ──
  const tableRows = (u: OrgUnit, depth: number): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];
    const pad = { paddingLeft: 12 + depth * 20 };
    rows.push(
      <tr key={`u${u.id}`} className="bg-slate-50/80 dark:bg-slate-900/60">
        <td className="py-2 pr-3 font-bold text-slate-900 dark:text-white" style={pad}>
          <span className="inline-flex items-center gap-1.5 flex-wrap"><Building2 className="w-3.5 h-3.5 text-indigo-600" />{u.name}{unitBadges(u)}</span>
        </td>
        <td className="py-2 px-3">{leaderLine(u)}</td>
        <td className="py-2 px-3"><ContactBits phone={u.phone} email={u.email} address={u.address} /></td>
        <td className="py-2 px-3 text-right whitespace-nowrap">{unitActions(u)}</td>
      </tr>,
    );
    for (const p of unitPositions(model, u.id)) {
      const ppl = positionPersons(model, p.id).filter(personHit);
      if (q && !norm(p.title).includes(q) && !ppl.length) continue;
      rows.push(
        <tr key={`p${p.id}`}>
          <td className="py-1.5 pr-3 text-slate-800 dark:text-slate-200" style={{ paddingLeft: 32 + depth * 20 }}>
            <span className="inline-flex items-center gap-1.5">{p.is_leader ? <Crown className="w-3.5 h-3.5 text-amber-500" /> : <Briefcase className="w-3.5 h-3.5 text-slate-400" />}{p.title}</span>
          </td>
          <td className="py-1.5 px-3 text-slate-900 dark:text-white">{ppl.length ? ppl.map((pr) => <div key={pr.id} className="flex items-center gap-1">{pr.name}{personActions(pr)}</div>) : <span className="italic text-slate-400">betöltetlen</span>}</td>
          <td className="py-1.5 px-3">{ppl.map((pr) => <div key={pr.id}><ContactBits phone={pr.phone} email={pr.email} room={pr.room} /></div>)}</td>
          <td className="py-1.5 px-3 text-right whitespace-nowrap">{posActions(p)}</td>
        </tr>,
      );
    }
    for (const pr of unitLoosePersons(model, u.id).filter(personHit)) {
      rows.push(
        <tr key={`x${u.id}-${pr.id}`}>
          <td className="py-1.5 pr-3 text-slate-500 italic" style={{ paddingLeft: 32 + depth * 20 }}>{pr.title || 'pozíció nélkül'}</td>
          <td className="py-1.5 px-3 text-slate-900 dark:text-white">{pr.name}</td>
          <td className="py-1.5 px-3"><ContactBits phone={pr.phone} email={pr.email} room={pr.room} /></td>
          <td className="py-1.5 px-3 text-right whitespace-nowrap">{personActions(pr)}</td>
        </tr>,
      );
    }
    for (const k of kidsOf(u.id)) rows.push(...tableRows(k, depth + 1));
    return rows;
  };

  const top = kidsOf(null);
  const orphanPositions = unitPositions(model, null).filter((p) => !q || norm(p.title).includes(q) || positionPersons(model, p.id).some(personHit));
  const orphanPeople = unitLoosePersons(model, null).filter(personHit);
  const hasOrphans = orphanPositions.length > 0 || orphanPeople.length > 0;

  if (!model.units.length && !hasOrphans) {
    return (
      <div className="py-14 text-center rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 text-sm text-slate-500">
        Még nincs szervezeti egység.
        {canEdit && <div className="mt-3"><button type="button" onClick={() => h.onAddUnit(null)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold cursor-pointer"><Plus className="w-3.5 h-3.5" /> Első egység felvétele</button></div>}
      </div>
    );
  }

  const orphanBlock = hasOrphans && (
    <div className="rounded-2xl border border-dashed border-amber-300 dark:border-amber-800 p-3 space-y-1.5 bg-amber-50/30 dark:bg-amber-950/10">
      <div className="text-xs font-bold text-amber-900 dark:text-amber-200">Egységhez nem rendelt pozíciók és munkatársak</div>
      {orphanPositions.map(positionRow)}
      {loosePeople(null)}
    </div>
  );

  if (mode === 'table') {
    return (
      <div className="space-y-3">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-950/40">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-800">
                <tr><th className="py-2 px-3">Egység / pozíció</th><th className="py-2 px-3">Munkatárs / vezető</th><th className="py-2 px-3">Elérhetőség</th><th className="py-2 px-3 text-right">Műveletek</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">{top.flatMap((u) => tableRows(u, 0))}</tbody>
            </table>
          </div>
        </div>
        {orphanBlock}
      </div>
    );
  }
  if (mode === 'accordion') return <div className="space-y-2">{top.map((u) => renderAccordion(u, 0))}{orphanBlock}</div>;
  return <div className="space-y-4">{top.map((u) => renderBlock(u, 0))}{orphanBlock}</div>;
};

export default OrgStructurePanel;
